(function () {
    'use strict';

    var GAP = 24;
    var GAP_MOBILE = 8;
    var STEP_DESKTOP = 32;
    var STEP_MOBILE = 16;
    var PEEK_GAP_DESKTOP = 33;
    var SIDE_INSET_DESKTOP = 41;
    var LEFT_PEEK_SCALE = 0.98;
    var STACK_SCALE = 224 / 288;
    var SWIPE_THRESHOLD = 40;
    var TRAIL_MOBILE = 16;   // set to (Figma space after peek) − 16px CSS padding

    class TestimonialsSlider extends HTMLElement {
        connectedCallback() {
            this.viewport = this.querySelector('[data-ts-viewport]');
            this.track = this.querySelector('[id^="Slider-"]');
            this.cards = Array.prototype.slice.call(
                this.querySelectorAll('[id^="Slide-"]')
            );
            this.prevBtn = this.querySelector('button[name="previous"]');
            this.nextBtn = this.querySelector('button[name="next"]');
            this.currentPageElement = this.querySelector('.slider-counter--current');
            this.pageTotalElement = this.querySelector('.slider-counter--total');

            if (!this.track || this.cards.length === 0) return;

            this.index = 0;
            this.elevated = null;      // card held above during a forward move
            this.elevateTimer = null;
            this.onResize = this.debounce(this.handleResize.bind(this), 120);
            window.addEventListener('resize', this.onResize);

            if (this.prevBtn) {
                this.prevBtn.addEventListener('click', this.prev.bind(this));
            }
            if (this.nextBtn) {
                this.nextBtn.addEventListener('click', this.next.bind(this));
            }

            if (this.pageTotalElement) {
                this.pageTotalElement.textContent = this.cards.length;
            }

            this.bindSwipe();
            this.bindReadMore();

            this.classList.add('is-resizing');
            this.layout();
            this.render();
            this.refreshReadMoreButtons();
            this.updateHeights(false);
            requestAnimationFrame(
                function () {
                    requestAnimationFrame(
                        function () {
                            this.classList.remove('is-resizing');
                        }.bind(this)
                    );
                }.bind(this)
            );

            window.addEventListener(
                'load',
                function () {
                    this.refreshReadMoreButtons();
                    this.updateHeights(false);
                }.bind(this),
                { once: true }
            );

            if (document.fonts && document.fonts.ready) {
                document.fonts.ready.then(
                    function () {
                        this.refreshReadMoreButtons();
                        this.updateHeights(false);
                    }.bind(this)
                );
            }
        }

        disconnectedCallback() {
            window.removeEventListener('resize', this.onResize);
            this.releaseElevated();
        }

        /* ----------------------------------------------------------------
           Layout math.

           The two ACTIVE cards get a fixed width/position that never
           changes with index: centering only has to fit
             card [gap] card  = C
           — no peek space baked in here. That's the fix: previously the
           centering math reserved an extra `step` on top of the two cards,
           which pulled `base` left (26 instead of 45) and, in an earlier
           version, made the whole pair resize/shift depending on state.

           Peeks are positioned relative to the CONTAINER'S OWN edges (0
           and C), completely independent of `base`/`cardW`. That's what
           lets "647px card, 45px inset, 32px peek" all stay true at
           every single index — nothing about the active pair has to move
           or resize to make room for a peek; the peek just lives in
           whatever space is left between the container edge and the
           active pair.
        ---------------------------------------------------------------- */
        layout() {
            this.containerW = this.viewport.clientWidth;
            this.isMobile = window.matchMedia('(max-width: 749px)').matches;
            this.perView = this.isMobile ? 1 : 2;
            this.step = this.isMobile ? STEP_MOBILE : STEP_DESKTOP;
            this.gap = this.isMobile ? GAP_MOBILE : GAP;

            var C = this.containerW;
            var pv = this.perView;
            // console.log('containerW', C, 'isMobile', this.isMobile, 'step', this.step, 'gap', this.gap);

            if (this.isMobile) {
                // Reserve trailing space after the right peek, the same way
                // the desktop branch reserves its side inset. Without this
                // the stack card's right edge is pinned to the container
                // edge (x + cardW === C) and the only visible gap is the
                // 16px CSS page padding.
                var cardWMobile =
                    C - 2 * this.step - this.gap - TRAIL_MOBILE;
                cardWMobile = Math.max(cardWMobile, 200);
                this.cardW = cardWMobile;
                this.off = 0;                      // keep the trail on the right
                this.base = this.step + this.gap;  // 24 — unchanged
            } else {
                // Desktop: reserve the design's 41px side inset BEFORE
                // splitting the row. That inset is the only space the peeks
                // have to live in:
                //   left peek reveals  base - PEEK_GAP_DESKTOP = 41 - 33 =  8px
                //   right stack reveals step                            = 32px
                // Both fit inside 41px, so peeks survive at every width.
                // At C = 1400 this returns exactly 647 — design unchanged.
                var avail =
                    (C - (pv - 1) * this.gap - 2 * SIDE_INSET_DESKTOP) / pv;
                var cardW = Math.min(647, Math.max(avail, 200));
                this.cardW = cardW;

                var inner = pv * cardW + (pv - 1) * this.gap;
                var off = Math.max(0, Math.round((C - inner) / 2));
                this.off = off;
                this.base = off; // constant at every index
            }

            this.maxIndex = Math.max(0, this.cards.length - this.perView);
            this.index = Math.min(this.index, this.maxIndex);
        }

        render() {
            var i = this.index;
            var cardW = this.cardW;
            var base = this.base;
            var self = this;
            var C = this.containerW;
            var lastRight = base + this.perView * cardW + (this.perView - 1) * this.gap;

            this.cards.forEach(function (card, j) {
                card.style.width = cardW + 'px';

                var rel = j - i;
                var x, scale, isPeek, z, origin;

                if (rel >= 0 && rel < self.perView) {
                    // fully visible card
                    x = base + rel * (cardW + self.gap);
                    scale = 1;
                    isPeek = false;
                    z = 8;
                    origin = 'center center';
                    // console.log('first slide', '1', 'rel', rel, 'x', x, 'scale', scale, 'z', z, 'origin', origin);
                } else if (rel === -1) {
                    // LEFT peek — anchored to the container's own left edge
                    // (x=0), independent of `base`. Right edge of the card
                    // lands at exactly `step`px, giving a clean, constant
                    // sliver regardless of where the active pair sits.
                    x = self.isMobile
                        ? self.off + self.step - cardW
                        : base - PEEK_GAP_DESKTOP - cardW;
                    scale = LEFT_PEEK_SCALE;
                    isPeek = true;
                    z = 3;
                    origin = 'right center';
                    // console.log('second slide', '2', 'rel', rel, 'x', x, 'scale', scale, 'z', z, 'origin', origin);
                } else if (rel < -1) {
                    // deeper off-screen left, continuing the linear track
                    x =
                        (self.isMobile
                            ? self.off + self.step - cardW
                            : base - PEEK_GAP_DESKTOP - cardW) +
                        (rel + 1) * (cardW + self.gap);
                    scale = LEFT_PEEK_SCALE;
                    isPeek = true;
                    z = 1;
                    origin = 'right center';
                    // console.log('third slide', '3', 'rel', rel, 'x', x, 'scale', scale, 'z', z, 'origin', origin);
                } else {
                    // RIGHT-SIDE STACK — relies on the ACTIVE 2nd card
                    // (opaque, higher z-index) covering most of an
                    // oversized, scaled-down stack card, leaving only the
                    // `step`px that protrudes past its right edge visible.
                    // That's why this anchors to `lastRight` (the active
                    // pair's own right edge), not the container edge —
                    // and why it's safe now: `base`/`cardW` are constant
                    // at every index, so `lastRight` is too.
                    var k = rel - self.perView;
                    x = lastRight + self.step - cardW;
                    scale = STACK_SCALE;
                    isPeek = true;
                    z = Math.max(7 - k, 1);
                    origin = 'right center';
                    // console.log('fourth slide', '4', 'rel', rel, 'x', x, 'scale', scale, 'z', z, 'origin', origin);
                }

                card.style.transformOrigin = origin;
                // card.style.zIndex = z;
                card.style.zIndex = card === self.elevated ? 9 : z;
                card.style.transform =
                    'translateX(' + Math.round(x) + 'px) scale(' + scale + ')';
                card.classList.toggle('is-peek', isPeek);
                card.classList.toggle('is-active', !isPeek);
                card.setAttribute('aria-hidden', isPeek ? 'true' : 'false');

                var btn = card.querySelector('[data-ts-readmore]');
                if (btn) btn.tabIndex = isPeek ? -1 : 0;
            });

            this.updateArrows();
            this.updateCounter();
        }

        updateArrows() {
            if (this.prevBtn) this.prevBtn.disabled = this.index <= 0;
            if (this.nextBtn) this.nextBtn.disabled = this.index >= this.maxIndex;
        }

        updateCounter() {
            if (this.currentPageElement) {
                this.currentPageElement.textContent = this.index + 1;
            }
        }

        prev() {
            if (this.index <= 0) return;
            this.releaseElevated();   // backward moves are correct as-is
            this.index--;
            this.render();
        }

        next() {
            if (this.index >= this.maxIndex) return;
            // Mobile only: the outgoing card becomes the left peek and must
            // stay on top while it slides off, revealing the next card
            // underneath. Drop `this.isMobile &&` to apply on desktop too.
            var outgoing = this.isMobile ? this.cards[this.index] : null;
            this.index++;
            this.render();
            if (outgoing) this.holdElevated(outgoing);
        }

        holdElevated(card) {
            var self = this;
            this.releaseElevated();
            this.elevated = card;
            this.render();   // re-applies z-index with the hold in place

            this.onElevatedEnd = function (e) {
                if (e.target !== card || e.propertyName !== 'transform') return;
                self.releaseElevated();
            };
            card.addEventListener('transitionend', this.onElevatedEnd);

            // transitionend never fires if the transition is cancelled or
            // `is-resizing` disabled it — 500ms duration + headroom.
            this.elevateTimer = setTimeout(function () {
                self.releaseElevated();
            }, 700);
        }

        releaseElevated() {
            if (!this.elevated) return;
            var card = this.elevated;
            this.elevated = null;
            clearTimeout(this.elevateTimer);
            this.elevateTimer = null;
            if (this.onElevatedEnd) {
                card.removeEventListener('transitionend', this.onElevatedEnd);
                this.onElevatedEnd = null;
            }
            this.render();   // restores the computed z-index
        }

        handleResize() {
            this.classList.add('is-resizing');
            this.layout();
            this.render();
            this.refreshReadMoreButtons();
            this.updateHeights(false);
            requestAnimationFrame(
                function () {
                    this.classList.remove('is-resizing');
                }.bind(this)
            );
        }

        bindSwipe() {
            var startX = 0;
            var startY = 0;
            var tracking = false;
            var self = this;

            this.viewport.addEventListener(
                'touchstart',
                function (e) {
                    startX = e.touches[0].clientX;
                    startY = e.touches[0].clientY;
                    tracking = true;
                },
                { passive: true }
            );

            this.viewport.addEventListener(
                'touchmove',
                function (e) {
                    if (!tracking) return;
                    var dx = e.touches[0].clientX - startX;
                    var dy = e.touches[0].clientY - startY;
                    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) {
                        e.preventDefault();
                    }
                },
                { passive: false }
            );

            this.viewport.addEventListener(
                'touchend',
                function (e) {
                    if (!tracking) return;
                    tracking = false;
                    var dx = e.changedTouches[0].clientX - startX;
                    if (Math.abs(dx) < SWIPE_THRESHOLD) return;
                    if (dx < 0) self.next();
                    else self.prev();
                },
                { passive: true }
            );
        }

        bindReadMore() {
            var self = this;
            this.querySelectorAll('[data-ts-readmore]').forEach(function (btn) {
                btn.addEventListener('click', function () {
                    var body = btn.closest('[data-ts-body]');
                    var label = btn.querySelector('[data-ts-readmore-label]');
                    var expanded = body.classList.toggle('is-expanded');
                    label.textContent = expanded ? 'Read Less' : 'Read More';
                    btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
                    self.updateHeights(true);
                });
            });
        }

        refreshReadMoreButtons() {
            this.cards.forEach(function (card) {
                var body = card.querySelector('[data-ts-body]');
                var text = card.querySelector('[data-ts-text]');
                var btn = card.querySelector('[data-ts-readmore]');
                if (!body || !text || !btn) return;

                if (body.classList.contains('is-expanded')) {
                    btn.hidden = false;
                    return;
                }

                var clampedH = text.clientHeight;

                var prevMax = text.style.maxHeight;
                var prevOverflow = text.style.overflow;
                text.style.maxHeight = 'none';
                text.style.overflow = 'visible';
                var fullH = text.scrollHeight;
                text.style.maxHeight = prevMax;
                text.style.overflow = prevOverflow;

                btn.hidden = fullH <= clampedH + 2;
            });
        }

        updateHeights(animate) {
            var self = this;

            if (!animate) this.classList.add('is-resizing');

            var prevHeights = this.cards.map(function (card) {
                return card.offsetHeight;
            });
            this.cards.forEach(function (card) {
                card.style.height = '';
                card.style.minHeight = '0';
            });
            var maxH = 0;
            this.cards.forEach(function (card) {
                maxH = Math.max(maxH, card.offsetHeight);
            });
            if (!this.isMobile) {
                maxH = Math.max(maxH, 288);
            }

            this.cards.forEach(function (card, i) {
                card.style.minHeight = prevHeights[i] + 'px';
            });

            void this.track.offsetHeight;

            requestAnimationFrame(function () {
                self.cards.forEach(function (card) {
                    card.style.minHeight = maxH + 'px';
                });
                self.track.style.minHeight = maxH + 'px';
                self.track.style.height = '';
                if (!animate) {
                    requestAnimationFrame(function () {
                        self.classList.remove('is-resizing');
                    });
                }
            });
        }

        debounce(fn, wait) {
            var t;
            return function () {
                clearTimeout(t);
                t = setTimeout(fn, wait);
            };
        }
    }

    if (!customElements.get('testimonials-slider')) {
        customElements.define('testimonials-slider', TestimonialsSlider);
    }

    if (window.Shopify && window.Shopify.designMode) {
        document.addEventListener('shopify:section:load', function (e) {
            var slider = e.target.querySelector('testimonials-slider');
            if (slider && slider.layout) {
                slider.classList.add('is-resizing');
                slider.layout();
                slider.render();
                slider.refreshReadMoreButtons();
                slider.updateHeights(false);
                requestAnimationFrame(function () {
                    slider.classList.remove('is-resizing');
                });
            }
        });
    }
})();